import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { RegisterScreen } from '@/src/presentation/screens/RegisterScreen';
import { ONBOARDING_FLAG_KEY } from '@/src/core/utils/storageKeys';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { isAuthRateLimitError, isEmailTakenError } from '@/src/core/utils/authErrors';

export default function RegisterContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async () => {
    if (!fullName.trim() || !email.trim() || !password.trim()) {
      platformAlert(t('common.error'), t('alerts.fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      platformAlert(t('common.error'), t('alerts.passwordsNotMatch'));
      return;
    }

    if (password.length < 6) {
      platformAlert(t('common.error'), t('alerts.passwordMinLength'));
      return;
    }

    try {
      setLoading(true);
      const { error } = await signUp(email.trim(), password, fullName.trim());

      if (error) {
        let msg: string;
        if (error.name === 'AuthRetryableFetchError') {
          msg = t('errors.networkError');
        } else if (isAuthRateLimitError(error)) {
          msg = t('errors.rateLimitAuth');
        } else if (isEmailTakenError(error)) {
          msg = t('errors.emailAlreadyRegistered');
        } else {
          msg = error.message || t('errors.somethingWentWrong');
        }
        platformAlert(t('alerts.registrationFailed'), msg);
      } else {
        await AsyncStorage.setItem(ONBOARDING_FLAG_KEY, 'true');
        platformAlert(
          t('common.success'),
          t('alerts.checkEmailVerify'),
          [{ text: t('common.ok'), onPress: () => router.replace('/auth/login') }]
        );
      }
    } catch (error: any) {
      platformAlert(t('common.error'), error.message || t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterScreen
      fullName={fullName}
      setFullName={setFullName}
      email={email}
      setEmail={setEmail}
      password={password}
      setPassword={setPassword}
      confirmPassword={confirmPassword}
      setConfirmPassword={setConfirmPassword}
      loading={loading}
      handleRegister={handleRegister}
      navigateToLogin={() => router.replace('/auth/login')}
      colors={colors}
    />
  );
}
